import { err, ok, type Result } from '@shared/result';

/**
 * El nombre de una persona, capturado a mano por alguien más.
 *
 * Existe por los contactos de emergencia, que son el único documento del portal
 * que un cliente escribe directo contra Firestore. Sin nada que lo revise, la
 * colección de producción acabó con `yfiuoi`, `21312321` y `12312sdsae21!"#$`
 * guardados como nombres de personas — y un contacto de emergencia que no se
 * puede leer no sirve para lo único que existe.
 *
 * **Es deliberadamente permisivo con la forma y estricto con el material.** Un
 * nombre puede llevar acentos, diéresis, eñes, apóstrofos (D'Angelo), guiones
 * (Ana-María) y puntos (`Ma. Guadalupe`, que en México se escribe así todo el
 * tiempo). Lo que no puede llevar son dígitos ni signos: ahí es donde vive la
 * diferencia entre un nombre y un tecleo.
 *
 * **Pide dos palabras** porque el campo se llama «Nombre completo» y el
 * parentesco tiene su propia casilla: quien escribe «Mamá» ya tiene dónde
 * decirlo, y un nombre suelto en una emergencia no alcanza para preguntar por
 * alguien. Es la única regla discutible de este archivo; se cambia quitando la
 * comprobación de `words.length`.
 *
 * No es un control de seguridad —las reglas de Firestore ponen el piso— sino lo
 * que convierte un error de captura en un mensaje inmediato y concreto.
 */

export type PersonNameError =
  | { readonly kind: 'empty' }
  | { readonly kind: 'too-short'; readonly raw: string }
  | { readonly kind: 'too-long'; readonly raw: string; readonly max: number }
  | { readonly kind: 'invalid-characters'; readonly raw: string }
  | { readonly kind: 'single-word'; readonly raw: string };

/** El mismo tope que la regla de Firestore para `emergencyContacts.name`. */
export const PERSON_NAME_MAX_LENGTH = 120;

/** Mínimo de letras, para que `A B` no pase por nombre completo. */
const MINIMUM_LETTERS = 3;

/**
 * Una palabra del nombre: empieza con letra y sigue con letras, marcas de
 * acento, apóstrofo, guion o punto. `\p{M}` está por los acentos escritos como
 * carácter aparte, que es lo que produce un teclado de macOS al componer.
 */
const WORD = /^\p{L}[\p{L}\p{M}'’.-]*$/u;

const LETTER = /\p{L}/gu;

export class PersonName {
  private constructor(
    /** Ya normalizado: sin espacios de sobra, ni al borde ni repetidos. */
    readonly value: string,
  ) {}

  static create(raw: string): Result<PersonName, PersonNameError> {
    const normalized = raw.trim().replace(/\s+/g, ' ');

    if (normalized.length === 0) return err({ kind: 'empty' });
    if (normalized.length > PERSON_NAME_MAX_LENGTH) {
      return err({ kind: 'too-long', raw, max: PERSON_NAME_MAX_LENGTH });
    }

    const words = normalized.split(' ');

    /*
     * El material antes que la forma: `asdf123` tiene que decir «hay caracteres
     * que no van», no «escribe apellido». Un mensaje que señala el problema
     * equivocado se lee como un formulario roto.
     */
    if (!words.every((word) => WORD.test(word))) {
      return err({ kind: 'invalid-characters', raw });
    }

    if ((normalized.match(LETTER) ?? []).length < MINIMUM_LETTERS) {
      return err({ kind: 'too-short', raw });
    }

    if (words.length < 2) return err({ kind: 'single-word', raw });

    return ok(new PersonName(normalized));
  }

  equals(other: PersonName): boolean {
    return this.value === other.value;
  }

  toString(): string {
    return this.value;
  }
}

/** El mensaje que va debajo del campo. Dice qué hacer, no qué pasó. */
export const personNameMessage = (error: PersonNameError): string => {
  switch (error.kind) {
    case 'empty':
      return 'Escribe el nombre de la persona.';
    case 'too-short':
      return 'El nombre es demasiado corto.';
    case 'too-long':
      return `El nombre no puede pasar de ${String(error.max)} caracteres.`;
    case 'invalid-characters':
      return 'Usa sólo letras: sin números ni signos.';
    case 'single-word':
      return 'Escribe nombre y apellido.';
  }
};
